import { useCallback, useRef, useState } from 'react';

import { AlertCircleIcon, ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { selectAccountSlug } from '@/redux/session/selectors';
import { isKeyEnter } from '@/util/util';

interface DeleteLanguageModelModalProps {
  languageModelId: string | null;
  onClose: () => void;
  onDeleted?: (languageModelId: string) => void;
}

export const DeleteLanguageModelModal = ({
  languageModelId,
  onClose,
  onDeleted,
}: DeleteLanguageModelModalProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { languageModel, languageModelBrains, deleteLanguageModel, deleteStatus } =
    useLanguageModels({ languageModelId: languageModelId ?? undefined });

  const [confirmText, setConfirmText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const hasConnectedAgents = languageModelBrains && languageModelBrains.length > 0;
  const confirmWord = t('common.delete');
  const isConfirmed = confirmText.trim().toUpperCase() === confirmWord.trim().toUpperCase();
  const isDeleting = deleteStatus === 'pending';

  const closeModal = useCallback(() => {
    setConfirmText('');
    onClose();
  }, [onClose]);

  const handleDelete = useCallback(() => {
    if (!languageModelId) {
      return;
    }
    deleteLanguageModel(languageModelId, {
      onSuccess: () => onDeleted?.(languageModelId),
      onError: (error) => toast.error(error.message),
      onSettled: closeModal,
    });
  }, [deleteLanguageModel, languageModelId, closeModal, onDeleted]);

  const handleOpenAutoFocus = useCallback(
    (e: Event) => {
      e.preventDefault();
      if (hasConnectedAgents) {
        inputRef.current?.focus();
      } else {
        cancelButtonRef.current?.focus();
      }
    },
    [hasConnectedAgents]
  );

  return (
    <Dialog open={!!languageModelId} onOpenChange={closeModal}>
      <DialogContent className="gap-6 sm:max-w-md" onOpenAutoFocus={handleOpenAutoFocus}>
        <DialogHeader>
          <DialogTitle>{t('developer_tools.delete_language_model')}</DialogTitle>
          <DialogDescription>
            {t('developer_tools.delete_language_models', { 0: languageModel?.name })}
          </DialogDescription>
        </DialogHeader>

        {hasConnectedAgents && (
          <div className="space-y-4">
            <Alert variant="destructive" className="border-destructive/50 text-destructive">
              <AlertCircleIcon size={16} />
              <AlertDescription>
                {t('developer_tools.delete_warning', { 0: languageModel?.model })}
              </AlertDescription>
            </Alert>

            <div className="space-y-2 py-4">
              <h3 className="text-sm leading-6 font-medium text-foreground-muted">
                {t('collections.connected_ai_agents')}
              </h3>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {languageModelBrains.map((agent) => (
                  <Button
                    asChild
                    key={agent.brain_id}
                    variant="link"
                    className="h-6 min-w-auto p-0"
                  >
                    <Link to={`/${slug}/ai-agents/${agent.brain_id}`} onClick={closeModal}>
                      {agent.name}
                      <ExternalLinkIcon size={16} />
                    </Link>
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="confirm-delete">
                {t('common.type_to_delete', { 0: confirmWord })}
              </Label>
              <Input
                id="confirm-delete"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                onKeyDown={(e) => {
                  if (isKeyEnter(e) && isConfirmed && !isDeleting) {
                    handleDelete();
                  }
                }}
                ref={inputRef}
                autoComplete="off"
                disabled={isDeleting}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button ref={cancelButtonRef} variant="ghost-neutral" onClick={closeModal}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={hasConnectedAgents ? !isConfirmed : false}
            isLoading={isDeleting}
          >
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
